import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { formatDateOnly } from "@/lib/dates";
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton } from "@/components/ui";
import { FieldAccessEnded } from "@/components/tester/chrome/field-access-ended";
import { CredentialFormDialog } from "@/components/tester/credentials/credential-form-dialog";
import { credentialExpiryVariant } from "@/components/tester/credentials/credential-expiry";
import { EvidenceList } from "@/components/tester/credentials/evidence-list";
import { useMyFieldCredentials } from "@/hooks/use-my-field-credentials";

export const Route = createFileRoute("/_company/credentials/$credentialId")({
  component: TesterCredentialDetailPage,
});

function DetailLoadingSkeleton() {
  return (
    <div className="space-y-6 p-4 sm:p-8">
      <Skeleton width="240px" height="36px" />
      <Skeleton variant="card" />
      <Skeleton variant="card" height="160px" />
    </div>
  );
}

interface DetailFieldProps {
  label: string;
  value: string;
}

function DetailField({ label, value }: DetailFieldProps) {
  return (
    <div>
      <dt className="text-sm font-medium text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-on-surface">{value}</dd>
    </div>
  );
}

/**
 * One credential, read-only, plus the certificates filed against it. This route
 * exists for the evidence surface: `listMyCredentialEvidence` and
 * `downloadMyCredentialEvidence` are the two field operations the phone never
 * wired, because a phone has nowhere to put a cert card it can render at size.
 *
 * The field stack exposes no by-id credential read, so the row is resolved out
 * of the same list the index screen renders — one query, two screens, and a
 * deep link lands on data the tester can already see. A `credentialId` with no
 * matching row is a real state (a stale link, a deleted row), so it gets its
 * own line rather than an empty page.
 *
 * Editing stays the dialog the list uses: a credential is personal, so every
 * row the caller can see is a row the caller owns.
 */
function TesterCredentialDetailPage() {
  const { credentialId } = Route.useParams();
  const { t } = useTranslation(["field-work", "common"]);

  const query = useMyFieldCredentials();
  const [formOpen, setFormOpen] = useState(false);

  const credential = query.data?.items.find((row) => row.id === credentialId) ?? null;

  if (query.error instanceof ApiError && query.error.status === 403) {
    return <FieldAccessEnded />;
  }

  if (query.isLoading) return <DetailLoadingSkeleton />;

  if (query.isError) {
    return (
      <div className="flex min-h-64 items-center justify-center p-4 sm:p-8">
        <p className="text-danger">{t("common:errors.somethingWentWrong")}</p>
      </div>
    );
  }

  if (!credential) {
    return (
      <div className="p-4 sm:p-8">
        <EmptyState title={t("field-work:credentials.notFound")} />
      </div>
    );
  }

  const variant = credentialExpiryVariant(credential.expires_at);
  const expiryLabel =
    variant === "danger"
      ? t("field-work:credentials.expired")
      : t("field-work:credentials.expires", { date: formatDateOnly(credential.expires_at) });

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader
        title={t(`field-work:credentials.scopeLabels.${credential.activity_scope}`)}
        subtitle={`${credential.certifying_body} · ${credential.identification_number}`}
        meta={<Badge variant={variant}>{expiryLabel}</Badge>}
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              setFormOpen(true);
            }}
          >
            {t("common:edit")}
          </Button>
        }
      />
      <Card>
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailField
            label={t("field-work:credentials.scopeLabel")}
            value={t(`field-work:credentials.scopeLabels.${credential.activity_scope}`)}
          />
          <DetailField
            label={t("field-work:credentials.certifyingBody")}
            value={credential.certifying_body}
          />
          <DetailField
            label={t("field-work:credentials.identificationNumber")}
            value={credential.identification_number}
          />
          <DetailField
            label={t("field-work:credentials.issuedAt")}
            value={credential.issued_at ? formatDateOnly(credential.issued_at) : "-"}
          />
          <DetailField
            label={t("field-work:credentials.expiresAt")}
            value={formatDateOnly(credential.expires_at)}
          />
        </dl>
      </Card>
      <EvidenceList credentialId={credential.id} />
      <CredentialFormDialog
        open={formOpen}
        credential={credential}
        onClose={() => {
          setFormOpen(false);
        }}
        onSaved={() => {
          setFormOpen(false);
        }}
      />
    </div>
  );
}
